import React, { useMemo } from 'react';

import { Block } from '@klp/design';
import { useTranslation } from 'react-i18next';
import { Chart } from 'react-google-charts';
import { KlpPalette } from '@klp/design/Theme/KlpPalette';

import { ContractLinesDateRange } from '../classes/ContractLinesDateRange';
import {
    getDefaultExpiryType,
    useFetchFrontpageAggregatesRequest,
} from '../utils';
import { useCurrentUser } from '@klp/auth';
import { useKlpStyles } from '@klp/design/Theme/useKlpStyles';
import { useGetFrontpageAggregatesQuery } from '../services/rtkApi';
import { LocalLoader } from '@klp/design/Loader';

export const options = {
    legend: { position: 'left' },
    colors: ['#0FB28A', '#e6693e', 'orange'],
    chartArea: { width: '90%', height: '80%' },
};

const styles = () => ({
    title: {
        textTransform: 'uppercase',
    },
    greenText: {
        color: KlpPalette.secondary.main,
    },
    customStyles: {
        '& svg > g > .google-visualization-tooltip': {
            pointerEvents: 'none',
        },
    },
});

export const ArealmessigUtleiegradSummary: React.FC = () => {
    const { t } = useTranslation();
    const { currentUser } = useCurrentUser();
    const classes = useKlpStyles({}, styles);
    const { frontpageAggregateRequest } = useFetchFrontpageAggregatesRequest();
    const {
        data: aggregateFrontpageData,
        isLoading: aggregateFrontpageLoading,
        isFetching: aggregateFrontpageIsFetching,
    } = useGetFrontpageAggregatesQuery(frontpageAggregateRequest, {
        skip: !currentUser,
    });

    const chartData = useMemo(() => {
        if (!aggregateFrontpageData) return undefined;

        const arealUtleid =
            aggregateFrontpageData.sumArealUtleid -
            aggregateFrontpageData.sumArealUnderUtloep;

        return [
            ['Type', 'm2'],
            [t('frontPage.expiredContracts.rented'), arealUtleid],
            [
                t('frontPage.expiredContracts.available'),
                aggregateFrontpageData.sumArealLedig,
            ],
            [
                t('frontPage.expiredContracts.rentedExpiring'),
                aggregateFrontpageData.sumArealUnderUtloep,
            ],
        ];
    }, [aggregateFrontpageData, t]);

    if (!currentUser) return null;

    return (
        <LocalLoader
            isLoading={
                aggregateFrontpageLoading || aggregateFrontpageIsFetching
            }
        >
            <Block
                marginTop="compact"
                klpTypographyVariant="xsmall"
                className={classes.title}
                data-cy="ContractsExpiringSummary__ContractsExpiringRentalRateTitle"
            >
                {t('frontPage.expiringContracts.yourRentalRateDate')}
                {' ' + ContractLinesDateRange.firstDayOfMonthLocaleFormatted()}
                {' - '}
                {' ' +
                    ContractLinesDateRange.fromExpiryType(
                        getDefaultExpiryType(currentUser),
                    ).toDateNextMonthLocaleFormatted()}
            </Block>
            {chartData && (
                <Chart
                    chartType="PieChart"
                    data={chartData}
                    options={options}
                    width="100%"
                    className={classes.customStyles}
                />
            )}
        </LocalLoader>
    );
};
